/**
 * Phase 3 forms (docs/04): Parts List, Field Report, Field Lube Report, Rental Check-Out/In and
 * signatures. Request bodies shared by the office API, the iPad's sync items and the web app.
 *
 * Numbers that end up in DECIMAL columns are validated as decimal strings (a JSON number is
 * accepted only if it prints exactly, e.g. 2.5 — never 0.30000000000000004), so the server can
 * build a Prisma.Decimal from the string and money never passes through a float.
 */
import { z } from 'zod';
export const SIGNATURE_FORMS = ['FIELD_REPORT', 'LUBE', 'RENTAL_OUT', 'RENTAL_IN'];
export const SignatureForm = z.enum(SIGNATURE_FORMS);
export const SIGNATURE_ROLES = ['TECHNICIAN', 'CUSTOMER'];
export const SignatureRole = z.enum(SIGNATURE_ROLES);
export const SIGNATURE_FORM_LABELS = {
    FIELD_REPORT: 'Field Report',
    LUBE: 'Field Lube Report',
    RENTAL_OUT: 'Rental Check-Out',
    RENTAL_IN: 'Rental Check-In',
};
export const RENTAL_PHASES = ['OUT', 'IN'];
export const CLEANLINESS = ['CLEAN', 'ADDITIONAL_TIME'];
export const CLEANLINESS_LABELS = {
    CLEAN: 'Clean',
    ADDITIONAL_TIME: 'Additional time required',
};
export const DAMAGE_BILLING = ['BILLED', 'WAIVED'];
/** Lubricant rows not in the profile list use this name plus `otherName` (paper: "Other"). */
export const OTHER_LUBRICANT = 'Other';
/** The cleanliness line in the rental damage list (it is not an inspection item). */
export const CLEANLINESS_ITEM = 'Cleanliness';
/** Photo slots for one rental inspection: the profile's required slots + one per attachment. */
export function rentalPhotoSlots(requiredSlots, attachments) {
    return [...requiredSlots, ...(attachments ?? []).map((a) => `Attachment: ${a}`)];
}
/**
 * The paper forms a job can need (docs/04). Which ones each job type needs is client data:
 * `jobForms` in the profile (jobType -> forms). The Parts List is on every job and is not listed.
 * The Field Report and the Shop Report are the same form (one `field_reports` row per job) with a
 * different title; a job type uses one or the other, never both.
 */
export const JOB_FORMS = ['field_report', 'shop_report', 'lube', 'rental'];
export const JobForm = z.enum(JOB_FORMS);
export const JOB_FORM_LABELS = {
    field_report: 'Field Report',
    shop_report: 'Shop Report',
    lube: 'Field Lube Report',
    rental: 'Rental Check-Out / Check-In',
};
/** The forms a job type needs. A job type the profile doesn't map (old data) gets the Field Report. */
export function formsFor(jobType, rules) {
    const f = rules.jobForms?.[jobType];
    return f && f.length ? f : ['field_report'];
}
/** Field Report or Shop Report for this job type, or null when it needs neither. */
export function reportFormFor(jobType, rules) {
    const f = formsFor(jobType, rules);
    return f.includes('shop_report') ? 'shop_report' : f.includes('field_report') ? 'field_report' : null;
}
/** Which rental phase a job type records, from the profile's closeRules (D16). */
export function rentalPhaseFor(jobType, rules) {
    if (rules.rentalCheckOutJobTypes.includes(jobType))
        return 'OUT';
    if (rules.rentalCheckInJobTypes.includes(jobType))
        return 'IN';
    return null;
}
/**
 * The forms whose signatures the close checks need for this job type (D24): each required form
 * has its own signature lines, like the paper. Field/Shop Report -> FIELD_REPORT, lube -> LUBE,
 * rental -> the phase's sheet.
 */
export function signatureFormsFor(jobType, rules) {
    const out = [];
    for (const f of formsFor(jobType, rules)) {
        let s = null;
        if (f === 'field_report' || f === 'shop_report')
            s = 'FIELD_REPORT';
        else if (f === 'lube')
            s = 'LUBE';
        else {
            const phase = rentalPhaseFor(jobType, rules);
            s = phase === 'OUT' ? 'RENTAL_OUT' : phase === 'IN' ? 'RENTAL_IN' : null;
        }
        if (s && !out.includes(s))
            out.push(s);
    }
    return out.length ? out : ['FIELD_REPORT'];
}
/** The job's first (main) signature form. */
export function signatureFormFor(jobType, rules) {
    return signatureFormsFor(jobType, rules)[0];
}
/** What a signature form is called on this job ("Shop Report" for a shop job's job sheet). */
export function signatureFormLabel(form, jobType, rules) {
    if (form === 'FIELD_REPORT')
        return JOB_FORM_LABELS[reportFormFor(jobType, rules) ?? 'field_report'];
    return SIGNATURE_FORM_LABELS[form];
}
/** Bulk or Buckets etc. — how a lubricant was supplied (profile `lube.supplyOptions`). */
export const SUPPLY_MAX = 20;
/** Field/Shop Report lubricant boxes, in paper order. */
export const REPORT_LUBES = ['eng', 'trans', 'hyd', 'coolant', 'other'];
export const REPORT_LUBE_LABELS = { eng: 'Eng.', trans: 'Trans.', hyd: 'Hyd.', coolant: 'Coolant', other: 'Other' };
// ---------- number helpers ----------
const decimal = (intDigits, frac, label) => z
    .union([z.string().trim(), z.number()])
    .transform((v) => String(v))
    .pipe(z.string().regex(new RegExp(`^\\d{1,${intDigits}}(\\.\\d{1,${frac}})?$`), `${label}: enter a number (up to ${frac} decimals)`));
/** Optional decimal: '' or null clears it. */
const optDecimal = (intDigits, frac, label) => z
    .union([z.literal(''), z.null(), decimal(intDigits, frac, label)])
    .optional()
    .transform((v) => (v === '' ? null : v));
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v ? v : null));
export const Money = (label) => optDecimal(10, 2, label);
export const Qty = decimal(8, 2, 'Qty').refine((v) => Number(v) > 0, 'Qty must be more than 0');
// ---------- Parts List ----------
/** What a mechanic enters (docs/04): qty, part no., # replaced, description. */
export const PartLineInput = z.object({
    qty: Qty,
    partNo: optText(80),
    replacedPartNo: optText(80),
    description: z.string().trim().min(1, 'Describe the part').max(300),
});
/** Office/Manager may also enter stock and prices. */
export const PartLineOfficeInput = PartLineInput.extend({
    stock: z.boolean().optional(),
    cost: Money('Cost'),
    list: Money('List'),
    sell: Money('Sell'),
});
export const PartLinePatch = PartLineOfficeInput.partial();
export const PartRemoveInput = z.object({ reason: z.string().trim().min(3, 'Say why the line is removed').max(2000) });
export const PartsMetaInput = z.object({
    purchaseOrder: optText(60),
    salesRep: optText(120),
    customerPo: optText(60),
    contactName: optText(120),
    contactPhone: optText(40),
});
export const NoPartsUsedInput = z.object({ value: z.boolean() });
// ---------- Field Report ----------
export const FieldReportInput = z.object({
    lubeEng: optText(60),
    lubeTrans: optText(60),
    lubeHyd: optText(60),
    lubeCoolant: optText(60),
    lubeOther: optText(120),
    /** Bulk / Buckets for each lubricant box (required for the boxes in fieldReport.supplyRequired). */
    lubeEngSupply: optText(SUPPLY_MAX),
    lubeTransSupply: optText(SUPPLY_MAX),
    lubeHydSupply: optText(SUPPLY_MAX),
    lubeCoolantSupply: optText(SUPPLY_MAX),
    lubeOtherSupply: optText(SUPPLY_MAX),
    mileage: optDecimal(9, 1, 'Mileage'),
    meals: Money('Meals'),
    motel: Money('Motel'),
});
// ---------- Field Lube Report ----------
/** Each item is Check, Change (or both), N/A, or blank. N/A can't be combined with Check/Change. */
export const LubeItemInput = z
    .object({ item: z.string().trim().min(1).max(80), checked: z.boolean(), changed: z.boolean(), na: z.boolean().optional().default(false) })
    .refine((i) => !(i.na && (i.checked || i.changed)), { message: 'An item marked N/A can’t also be Check or Change' });
export const LubeLubricantInput = z.object({
    lubricant: z.string().trim().min(1).max(60),
    otherName: z.string().trim().max(80).optional().default(''),
    gallons: z.union([z.literal(''), decimal(8, 2, 'Gallons')]).transform((v) => (v === '' ? '0' : v)),
    /** Bulk / Buckets (profile `lube.supplyOptions`); required for `lube.supplyRequired` with gallons. */
    supply: optText(SUPPLY_MAX),
});
export const LubeSaveInput = z.object({
    customerEquipmentNo: optText(80),
    typeOfService: optText(120),
    nextService: optText(120),
    equipHours: optDecimal(9, 1, 'Hours'),
    remarks: optText(10_000),
    items: z.array(LubeItemInput).max(100).optional(),
    lubricants: z.array(LubeLubricantInput).max(100).optional(),
});
// ---------- Rental Check-Out / Check-In ----------
export const RentalItemInput = z.object({
    item: z.string().trim().min(1).max(80),
    status: z.enum(['OK', 'ISSUE']),
    comment: optText(2000),
});
export const RentalSaveInput = z.object({
    hours: optDecimal(9, 1, 'Machine hours'),
    fuelLevel: optText(20),
    fuelGallonsAdded: optDecimal(8, 2, 'Gallons added'),
    defLevel: optText(20),
    defJugsAdded: optDecimal(8, 2, 'Jugs added'),
    cleanliness: z.enum(CLEANLINESS).nullable().optional(),
    operatorsManual: z.boolean().nullable().optional(),
    attachments: z.array(z.string().trim().min(1).max(60)).max(10).optional(),
    initials: optText(10),
    notes: optText(10_000),
    items: z.array(RentalItemInput).max(50).optional(),
});
export const RentalLinkInput = z.object({
    agreementNo: optText(60),
    /** Check-in job only: pair with this rental (an open check-out on the same machine). */
    rentalId: z.number().int().positive().optional(),
});
/** Manager marks a new-damage line (an inspection item, or "Cleanliness") Billed or Waived. */
export const DamageBillingInput = z.object({
    item: z.string().trim().min(1).max(80),
    billing: z.enum(DAMAGE_BILLING),
});
// ---------- Signatures ----------
export const SignatureInput = z.object({
    form: SignatureForm,
    role: SignatureRole,
    /** Required for a customer; a mechanic's own signature uses their name. */
    signerName: z.string().trim().max(120).optional(),
});
/** Office capture: the PNG from the signature pad as a data URL. */
export const OfficeSignatureInput = SignatureInput.extend({
    image: z.string().regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Signature image must be a PNG').max(900_000),
});
export const CustomerNotPresentInput = z.object({
    /** null clears it (e.g. the customer came back and signed). */
    reason: z.string().trim().min(10, 'Say why the customer could not sign (at least 10 characters)').max(2000).nullable(),
});
//# sourceMappingURL=forms.js.map